/**
 * ReviewMode — full-screen side-by-side reviewer for a queue of timesheets.
 *
 * Keyboard: ←/→ navigate, Enter confirm+next, Del skip+next, Esc exit.
 *
 * P32 (Feb 27, 2026): Refactored from a 516-line single-file component
 * into an orchestrator + 4 sub-components + `utils.js`:
 *   - utils.js           : pure date/time math (DAY_ORDER, time parsing,
 *                          minutes→units, chronological sort)
 *   - TopBar             : queue progress + keyboard hints
 *   - ScanViewer         : 70% left pane (image + zoom controls)
 *   - ReviewFormPane     : 30% right pane (AI hints + selects + entries)
 *   - ReviewActionBar    : bottom Prev/Skip/Delete/Confirm strip
 * Orchestrator keeps the API calls + state + keyboard wiring.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Spinner } from '@phosphor-icons/react';

import { entryUnits, sortChronologically } from './review/utils';
import TopBar from './review/TopBar';
import ScanViewer from './review/ScanViewer';
import ReviewFormPane from './review/ReviewFormPane';
import ReviewActionBar from './review/ReviewActionBar';

export const ReviewMode = ({
  queue, // array of timesheet IDs to review (in order)
  patients,
  employees,
  onClose,
  onRefresh,
}) => {
  const [index, setIndex] = useState(0);
  const [ts, setTs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leftPct, setLeftPct] = useState(70);
  const dividerDrag = useRef(false);
  const [patientId, setPatientId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [entries, setEntries] = useState([]);
  const [aideSig, setAideSig] = useState(false);
  const [supSig, setSupSig] = useState(false);
  // OCR field-provenance: which form field the reviewer is editing —
  // its bounding box lights up on the scan.
  const [activeFieldKey, setActiveFieldKey] = useState(null);

  const currentId = queue[index];
  const total = queue.length;
  const finished = index >= total;

  // ── Load the current timesheet ──
  const loadCurrent = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const res = await api.get(`/timesheets/${currentId}`);
      const t = res.data;
      setTs(t);
      const extracted = t.extracted_data || {};
      setPatientId(t.inline_patient_id || t.patient_match?.id || '');
      setEmployeeId(t.inline_employee_id || t.employee_match?.id || '');
      setEntries(
        sortChronologically(extracted.entries || []).map((e, i) => ({ _idx: i, ...e }))
      );
      setAideSig(t.aide_signature_completed ?? !!extracted.signature_present);
      setSupSig(t.supervisor_signature_completed ?? !!extracted.supervisor_signature_present);
    } catch {
      toast.error('Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  }, [currentId]);

  useEffect(() => { loadCurrent(); }, [loadCurrent]);

  // ── Queue navigation ──
  const goNext = useCallback(() => {
    if (index + 1 >= total) {
      toast.success('Reached end of queue');
      onClose();
      onRefresh?.();
    } else {
      setIndex(index + 1);
    }
  }, [index, total, onClose, onRefresh]);

  const goPrev = useCallback(() => {
    if (index > 0) setIndex(index - 1);
  }, [index]);

  const handleConfirm = useCallback(async () => {
    if (!ts) return;
    if (!patientId) { toast.error('Select a patient'); return; }
    if (!employeeId) { toast.error('Select an employee'); return; }
    setSaving(true);
    try {
      await api.put(`/timesheets/${ts.id}/confirm`, {
        patient_id: patientId,
        employee_id: employeeId,
        entries: entries.map(({ _idx, ...rest }) => rest),
        week_start_date: ts.extracted_data?.week_start_date,
        week_end_date: ts.extracted_data?.week_end_date,
        aide_signature_completed: aideSig,
        supervisor_signature_completed: supSig,
      });
      toast.success(`Confirmed (${index + 1}/${total})`);
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirmation failed');
    } finally {
      setSaving(false);
    }
  }, [ts, patientId, employeeId, entries, aideSig, supSig, index, total, goNext]);

  const handleSkip = useCallback(() => {
    toast.info(`Skipped ${index + 1}/${total}`);
    goNext();
  }, [goNext, index, total]);

  const handleDelete = useCallback(async () => {
    if (!ts) return;
    if (!window.confirm('Delete this timesheet and continue?')) return;
    try {
      await api.delete(`/timesheets/${ts.id}`);
      toast.success('Deleted');
      goNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  }, [ts, goNext]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e) => {
      // Don't intercept typing in inputs
      const tag = (e.target?.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      if (e.key === 'Escape') { onClose(); return; }
      if (isTyping && e.key !== 'Escape') {
        // Still allow Ctrl+Enter to confirm from inside an input
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          e.preventDefault();
          handleConfirm();
        }
        return;
      }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); handleSkip(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext, handleConfirm, handleSkip]);

  const updateEntry = (idx, field, value) => {
    setEntries((prev) => prev.map((e) => (e._idx === idx ? { ...e, [field]: value } : e)));
  };

  // Resizable split: drag the divider between scan and form panes.
  useEffect(() => {
    const onMove = (e) => {
      if (!dividerDrag.current) return;
      const pct = (e.clientX / window.innerWidth) * 100;
      setLeftPct(Math.min(80, Math.max(35, pct)));
    };
    const onUp = () => {
      dividerDrag.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const totalUnits = entries.reduce((a, e) => a + entryUnits(e), 0);

  // Build the highlight list from persisted OCR provenance: header field
  // boxes (field_boxes) + per-entry row boxes (entry.bbox), all on the
  // 0-1000 normalized page grid.
  const highlights = useMemo(() => {
    if (!ts) return [];
    const list = [];
    const fb = ts.extracted_data?.field_boxes || {};
    [
      ['patient_name', 'Patient'],
      ['staff_name', 'Staff'],
      ['week_start_date', 'Week start'],
      ['week_end_date', 'Week end'],
      ['total_weekly_hours', 'Total hours'],
    ].forEach(([key, label]) => {
      if (Array.isArray(fb[key]) && fb[key].length === 4) {
        list.push({ id: key, label, box: fb[key] });
      }
    });
    entries.forEach((e) => {
      if (Array.isArray(e.bbox) && e.bbox.length === 4) {
        list.push({ id: `entry-${e._idx}`, label: e.date || 'Visit', box: e.bbox });
      }
    });
    return list;
  }, [ts, entries]);

  if (finished) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/95 flex flex-col" data-testid="review-mode">
      <TopBar index={index} total={total} onClose={onClose} />
      <div className="flex-1 flex overflow-hidden">
        <ScanViewer
          loading={loading}
          timesheetId={ts?.id}
          widthPct={leftPct}
          highlights={highlights}
          activeHighlightId={activeFieldKey}
        />
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panels"
          onMouseDown={() => {
            dividerDrag.current = true;
          }}
          className="w-1.5 bg-slate-700 hover:bg-indigo-500 cursor-col-resize shrink-0 transition-colors"
          data-testid="review-pane-divider"
        />
        <div className="bg-white flex flex-col overflow-hidden" style={{ width: `${100 - leftPct}%` }}>
          {loading || !ts ? (
            <div className="flex-1 flex items-center justify-center">
              <Spinner size={24} className="animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              <ReviewFormPane
                ts={ts}
                patients={patients}
                employees={employees}
                patientId={patientId}
                setPatientId={setPatientId}
                employeeId={employeeId}
                setEmployeeId={setEmployeeId}
                entries={entries}
                updateEntry={updateEntry}
                totalUnits={totalUnits}
                aideSig={aideSig}
                setAideSig={setAideSig}
                supSig={supSig}
                setSupSig={setSupSig}
                onFieldFocus={setActiveFieldKey}
              />
              <ReviewActionBar
                index={index}
                saving={saving}
                onPrev={goPrev}
                onSkip={handleSkip}
                onDelete={handleDelete}
                onConfirm={handleConfirm}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
